// Separations panel (Task 2).
// One row per palette color: eye toggle, swatch (opens color editor), hex,
// mask thumbnail, coverage %, drag handle.
// Display order is REVERSED vs. internal layer order: the top row is the
// topmost layer, paper (layer 0 = bottom) sits pinned at the bottom of the
// list — matching the Photoshop-style layer stack.
// "Apply" in the editor recolors the ACTIVE colorway only; alpha maps are
// not touched by anything in this component.

import React, { useMemo, useRef, useState } from 'react';
import type { LabColor, PaletteColor, SeparationOptions } from '@imagelayerdecomposer/core';
import { labToHex } from '@imagelayerdecomposer/core';
import ColorEditorPopover, { type SwatchLibrary } from './ColorEditorPopover';

interface SeparationsPanelProps {
  colors: PaletteColor[];
  visibleMasks: boolean[];
  masks: Uint8ClampedArray[];
  maskWidth: number;
  maskHeight: number;
  coverage: number[];                   // mean alpha per layer, percent
  onToggle: (layerIndex: number) => void;
  onRecolor: (layerIndex: number, lab: LabColor) => void;
  onReorder: (fromLayer: number, toLayer: number) => void;
  options: SeparationOptions;
  onOptionsChange: (patch: Partial<SeparationOptions>) => void;
}

interface EditingState {
  layer: number;
  top: number;
  left: number;
}

// Small grayscale preview of one alpha mask, scaled down to fit the row.
function MaskThumb({
  mask,
  width,
  height,
}: {
  mask?: Uint8ClampedArray;
  width: number;
  height: number;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  React.useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !mask || width < 1 || height < 1) return;
    // Draw at native mask resolution offscreen, then scale into the row thumb
    const off = document.createElement('canvas');
    off.width = width;
    off.height = height;
    const octx = off.getContext('2d');
    if (!octx) return;
    const img = octx.createImageData(width, height);
    img.data.set(mask.subarray(0, width * height * 4));
    octx.putImageData(img, 0, 0);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(off, 0, 0, canvas.width, canvas.height);
  }, [mask, width, height]);

  return <canvas ref={ref} width={34} height={22} className="mask-thumb" title="Mask" />;
}

export default function SeparationsPanel({
  colors,
  visibleMasks,
  masks,
  maskWidth,
  maskHeight,
  coverage,
  onToggle,
  onRecolor,
  onReorder,
  options,
  onOptionsChange,
}: SeparationsPanelProps) {
  const [dragLayer, setDragLayer] = useState<number | null>(null);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [library, setLibrary] = useState<SwatchLibrary | null>(null);

  const n = colors.length;
  // Display order: top row = highest layer index
  const displayLayers = useMemo(
    () => colors.map((_, i) => n - 1 - i).filter((l) => l >= 0),
    [colors, n]
  );

  const handleModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onOptionsChange({ mode: e.target.value as 'flat' | 'soft' });
  };

  const handleNColorsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    if (Number.isFinite(val)) {
      onOptionsChange({ nColors: Math.max(2, Math.min(20, val)) });
    }
  };

  const handleNearestKChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onOptionsChange({ nearestK: parseInt(e.target.value, 10) });
  };

  const openEditor = (layer: number, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    setEditing({ layer, top: r.top, left: r.left });
  };

  const editingColor = editing ? colors[editing.layer] : null;
  const editingLabel = editing
    ? editing.layer === 0
      ? 'Paper (bottom layer)'
      : `Color ${editing.layer}`
    : '';

  return (
    <div className="panel">
      <div className="panel-header">
        <span>Separations</span>
      </div>
      <div className="control-row">
        <label>Mode:</label>
        <select value={options.mode} onChange={handleModeChange}>
          <option value="soft">Soft</option>
          <option value="flat">Flat</option>
        </select>
      </div>
      <div className="control-row">
        <label>Colors (N):</label>
        <input
          type="number"
          min="2"
          max="20"
          value={options.nColors}
          onChange={handleNColorsChange}
        />
      </div>
      <div className="control-row">
        <label>Nearest K:</label>
        <select value={options.nearestK} onChange={handleNearestKChange}>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
        </select>
      </div>

      <div className="layers-caption">Layers (top → bottom)</div>

      <div className="layer-list">
        {displayLayers.map((layer) => {
          const color = colors[layer];
          const isPaper = color.isPaper || layer === 0;
          const canDrag = !isPaper; // paper pinned at the bottom
          const hex = safeHex(color.lab);
          return (
            <div
              key={color.id}
              className={`color-row${dragLayer === layer ? ' dragging' : ''}`}
              draggable={canDrag}
              onDragStart={(e) => {
                if (!canDrag) {
                  e.preventDefault();
                  return;
                }
                setDragLayer(layer);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(layer));
              }}
              onDragEnd={() => setDragLayer(null)}
              onDragOver={(e) => {
                if (dragLayer !== null && dragLayer !== layer) e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
                if (!Number.isFinite(from) || from === layer) return;
                // Dropping onto the paper row means "just above the paper"
                const target = layer === 0 ? 1 : layer;
                if (from === target) return;
                onReorder(from, target);
                setDragLayer(null);
              }}
            >
              <button
                className={`eye${visibleMasks[layer] ? '' : ' off'}`}
                title={visibleMasks[layer] ? 'Hide mask' : 'Show mask'}
                onClick={() => onToggle(layer)}
              >
                {/* Eye = mask overlay visibility. Hidden state: dimmed eye with
                    a diagonal slash — NOT a "disabled" icon, the mask is simply
                    not drawn on the canvas until you click it. */}
                <span className={visibleMasks[layer] ? 'eye-glyph' : 'eye-glyph off'}>
                  👁
                </span>
              </button>
              <div
                className="swatch"
                style={{ backgroundColor: hex }}
                title={`${hex} — click to edit`}
                onClick={(e) => openEditor(layer, e.currentTarget as HTMLElement)}
              />
              <div className="color-info">
                <div className="color-name">
                  {isPaper ? 'Paper' : `Color ${layer}`}
                  <span className="hex">{hex}</span>
                </div>
                <div className="lab-values">
                  L:{color.lab.L.toFixed(0)} a:{color.lab.a.toFixed(0)} b:{color.lab.b.toFixed(0)}
                </div>
              </div>
              <MaskThumb mask={masks[layer]} width={maskWidth} height={maskHeight} />
              <div className="coverage" title="Coverage (mean alpha)">
                {coverage[layer] !== undefined ? `${coverage[layer].toFixed(1)}%` : '—'}
              </div>
              <div
                className="drag-handle"
                title={canDrag ? 'Drag to reorder (paper stays at bottom)' : 'Paper is pinned to the bottom'}
              >
                {canDrag ? '⠿' : '🔒'}
              </div>
            </div>
          );
        })}
      </div>

      {editingColor && (
        <ColorEditorPopover
          color={editingColor}
          label={`${editingLabel} · active colorway`}
          anchor={{ top: editing!.top, left: editing!.left }}
          library={library}
          onLibraryChange={setLibrary}
          onApply={(lab) => onRecolor(editing!.layer, lab)}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function safeHex(lab: LabColor): string {
  try {
    return labToHex(lab);
  } catch {
    return '#888888';
  }
}
